import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import VideoLightbox from './VideoLightbox';
import MediaKitInterestTags from './MediaKitInterestTags';
import MediaKitSocialOverview from './MediaKitSocialOverview';
import MediaKitHighlightCards from './MediaKitHighlightCards';
import MediaKitPhotoCollage from './MediaKitPhotoCollage';
import MediaKitIntroReel from './MediaKitIntroReel';
import BrandBanner from './BrandBanner';
import { fetchPublicMediaKit, buildPublicMediaKitPhotoUrl, MediaKitPublicData } from '../services/mediaKitService';
import { hasMediaKitSocials } from '../utils/mediaKitFormUtils';
import '../styles/media-kit.css';

function buildBannerSubtitle(data: Pick<MediaKitPublicData, 'graduationYear' | 'sport'>): string {
    const classOf = data.graduationYear ? `Class of ${data.graduationYear}` : '';
    const sport = data.sport?.trim() || '';
    if (classOf && sport) {
        return `${classOf} | ${sport}`;
    }
    return classOf || sport || 'ATHLETE';
}

function MediaKitPublicSidebar({ data }: { data?: MediaKitPublicData | null }) {
    const displayName = data
        ? `${data.firstName || ''} ${data.lastName || ''}`.trim() || 'Athlete'
        : 'Media Kit';

    return (
        <aside className="media-kit-public-sidebar">
            <BrandBanner
                displayName={displayName}
                hasProfile={true}
                profileImageUrl={data?.profileImageUrl ?? undefined}
                subtitleOverride={data ? buildBannerSubtitle(data) : 'ATHLETE'}
                className="media-kit-public-brand-banner"
            />
            {data?.email && (
                <div className="media-kit-public-contact">
                    <h3>Contact</h3>
                    <a href={`mailto:${data.email}`}>{data.email}</a>
                </div>
            )}
        </aside>
    );
}

function MediaKitPublicMain({ children }: { children: React.ReactNode }) {
    return <main className="media-kit-public-main">{children}</main>;
}

export default function MediaKitPublicPage() {
    const { athleteName, uuid } = useParams<{ athleteName: string; uuid: string }>();
    const [data, setData] = useState<MediaKitPublicData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showVideo, setShowVideo] = useState(false);

    useEffect(() => {
        const load = async () => {
            try {
                setLoading(true);
                const kit = await fetchPublicMediaKit(athleteName ?? '', uuid ?? '');
                setData(kit);
            } catch {
                setError('Media kit not found or link has expired.');
            } finally {
                setLoading(false);
            }
        };
        if (athleteName && uuid) {
            load();
        }
    }, [athleteName, uuid]);

    if (loading) {
        return (
            <div className="media-kit-public-page">
                <MediaKitPublicSidebar />
                <MediaKitPublicMain>
                    <p className="media-kit-public-status">Loading...</p>
                </MediaKitPublicMain>
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className="media-kit-public-page">
                <MediaKitPublicSidebar />
                <MediaKitPublicMain>
                    <p className="media-kit-public-status">{error ?? 'Not found'}</p>
                </MediaKitPublicMain>
            </div>
        );
    }

    const sportYear = [data.sport, data.graduationYear].filter(Boolean).join(' | ');
    const aboutYou = data.aboutYou?.trim() ?? '';
    const hasAbout = aboutYou.length > 0;
    const hasPhotos = data.photos.length > 0;
    const hasConnect = hasMediaKitSocials(data.socials);
    const hasInterests = data.interests.length > 0;
    const hasIntroReel = Boolean(data.introVideoUrl?.trim());
    const hasHighlights = data.highlights.length > 0;
    const publicPhotos = data.photos.map((photo) => ({
        ...photo,
        imageUrl: buildPublicMediaKitPhotoUrl(uuid ?? '', photo.id),
    }));

    return (
        <div className="media-kit-public-page" data-testid="media-kit-public-page">
            <MediaKitPublicSidebar data={data} />

            <MediaKitPublicMain>
                <div className="media-kit-view">
                    <div className="media-kit-section media-kit-profile-header">
                        <div className="media-kit-profile-copy">
                            <h1 className="media-kit-name">
                                {data.firstName && <span className="media-kit-name-line">{data.firstName}</span>}
                                {data.lastName && <span className="media-kit-name-line">{data.lastName}</span>}
                            </h1>
                            {sportYear && <p className="media-kit-sport-year">{sportYear}</p>}
                            {hasAbout && <p className="media-kit-about">{aboutYou}</p>}
                        </div>
                        {hasPhotos && <MediaKitPhotoCollage photos={publicPhotos} />}
                    </div>

                    {hasConnect && (
                        <div className="media-kit-section media-kit-connect-section">
                            <h3>Connect</h3>
                            <MediaKitSocialOverview socials={data.socials} />
                        </div>
                    )}

                    {(hasInterests || hasIntroReel) && (
                        <div className={hasInterests && hasIntroReel ? 'media-kit-two-col' : undefined}>
                            {hasInterests && (
                                <div className="media-kit-section">
                                    <h3>Interests</h3>
                                    <MediaKitInterestTags interests={data.interests} />
                                </div>
                            )}
                            {hasIntroReel && data.introVideoUrl && (
                                <div className="media-kit-section">
                                    <h3>Intro Reel</h3>
                                    <MediaKitIntroReel
                                        introVideoUrl={data.introVideoUrl}
                                        onPlay={() => setShowVideo(true)}
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    {hasHighlights && (
                        <div className="media-kit-highlights-section">
                            <div className="media-kit-highlights-heading">
                                <h3 className="media-kit-highlights-title">Past Highlights</h3>
                            </div>
                            <MediaKitHighlightCards highlights={data.highlights} />
                        </div>
                    )}
                </div>
            </MediaKitPublicMain>

            {showVideo && data.introVideoUrl && (
                <VideoLightbox
                    open={showVideo}
                    resource={{ id: 0, title: 'Intro Reel', vimeoUrl: data.introVideoUrl }}
                    onClose={() => setShowVideo(false)}
                />
            )}
        </div>
    );
}
